import { useState, useEffect } from 'react';
import type { AdminVideo, VideoCategory, VideoStatus, VideoSourceType } from '@/mocks/adminVideos';
import { categoryLabels, categoryIcons, statusLabels, sourceTypeLabels, sourceTypeIcons } from '@/mocks/adminVideos';
import { SwalAlert } from '@/hooks/useSweetAlert';

interface VideoFormModalProps {
  open: boolean;
  video: AdminVideo | null;
  categories: VideoCategory[];
  onClose: () => void;
  onSave: (data: {
    title: string;
    description: string;
    category: VideoCategory;
    sourceType: VideoSourceType;
    sourceUrl: string;
    duration: string;
    status: VideoStatus;
    isFeatured: boolean;
    scheduledAt?: string;
  }) => void;
}

const allSourceTypes: VideoSourceType[] = ['upload', 'youtube', 'facebook'];

export default function VideoFormModal({ open, video, categories, onClose, onSave }: VideoFormModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<VideoCategory>('programas');
  const [sourceType, setSourceType] = useState<VideoSourceType>('upload');
  const [sourceUrl, setSourceUrl] = useState('');
  const [duration, setDuration] = useState('');
  const [status, setStatus] = useState<VideoStatus>('borrador');
  const [isFeatured, setIsFeatured] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const isEditing = video !== null;

  useEffect(() => {
    if (open) {
      if (video) {
        setTitle(video.title);
        setDescription(video.description);
        setCategory(video.category);
        setSourceType(video.sourceType);
        setSourceUrl(video.sourceUrl);
        setDuration(video.duration);
        setStatus(video.status);
        setIsFeatured(video.isFeatured);
        setScheduledAt(video.scheduledAt || '');
      } else {
        setTitle('');
        setDescription('');
        setCategory('programas');
        setSourceType('upload');
        setSourceUrl('');
        setDuration('');
        setStatus('borrador');
        setIsFeatured(false);
        setScheduledAt('');
      }
      setErrors({});
    }
  }, [open, video]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!title.trim()) newErrors.title = 'El título es obligatorio';
    if (title.length > 120) newErrors.title = 'Máximo 120 caracteres';
    if (!description.trim()) newErrors.description = 'La descripción es obligatoria';
    if (description.length > 500) newErrors.description = 'Máximo 500 caracteres';
    if (sourceType !== 'upload' && !sourceUrl.trim()) newErrors.sourceUrl = 'La URL es obligatoria para fuentes externas';
    if (!duration.trim()) newErrors.duration = 'La duración es obligatoria';
    if (status === 'programado' && !scheduledAt) newErrors.scheduledAt = 'La fecha de programación es obligatoria';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    onSave({
      title: title.trim(),
      description: description.trim(),
      category,
      sourceType,
      sourceUrl: sourceUrl.trim(),
      duration: duration.trim(),
      status,
      isFeatured,
      scheduledAt: status === 'programado' ? scheduledAt : undefined,
    });
    onClose();
    setSaving(false);
    await SwalAlert.toast(isEditing ? 'Video actualizado.' : 'Video agregado.', 'success');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[10vh] overflow-y-auto">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-lg overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div>
            <h3 className="text-base font-bold text-foreground-950">
              {isEditing ? 'Editar video' : 'Subir video'}
            </h3>
            <p className="text-[11px] text-foreground-400 mt-0.5">
              {isEditing ? 'Modificá los datos del video' : 'Completá los datos para agregar un nuevo video'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Título */}
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Título</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Ej: Tardes de Pop - Programa completo"
              className={`w-full px-3 py-2.5 rounded-xl bg-background-100 border text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors ${
                errors.title ? 'border-red-400' : 'border-background-200'
              }`}
            />
            <div className="flex items-center justify-between mt-1">
              {errors.title && <p className="text-red-500 text-[10px]">{errors.title}</p>}
              <span className="text-[9px] text-foreground-400 ml-auto">{title.length}/120</span>
            </div>
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Descripción</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Describí el contenido del video..."
              className={`w-full px-3 py-2.5 rounded-xl bg-background-100 border text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors resize-none ${
                errors.description ? 'border-red-400' : 'border-background-200'
              }`}
            ></textarea>
            <div className="flex items-center justify-between mt-1">
              {errors.description && <p className="text-red-500 text-[10px]">{errors.description}</p>}
              <span className="text-[9px] text-foreground-400 ml-auto">{description.length}/500</span>
            </div>
          </div>

          {/* Categoría */}
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Categoría</label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(cat)}
                  className={`flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-xl border cursor-pointer transition-all ${
                    category === cat
                      ? 'border-accent-500 bg-accent-50 text-accent-700'
                      : 'border-background-200 bg-background-100 text-foreground-500 hover:bg-background-200'
                  }`}
                >
                  <i className={`${categoryIcons[cat]} text-lg`}></i>
                  <span className="text-[10px] font-semibold whitespace-nowrap">{categoryLabels[cat]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tipo de fuente */}
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Origen del video</label>
            <div className="flex items-center gap-2">
              {allSourceTypes.map((src) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setSourceType(src)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border cursor-pointer transition-all text-xs font-semibold ${
                    sourceType === src
                      ? 'border-secondary-500 bg-secondary-50 text-secondary-700'
                      : 'border-background-200 bg-background-100 text-foreground-500 hover:bg-background-200'
                  }`}
                >
                  <i className={`${sourceTypeIcons[src]} text-sm`}></i>
                  {sourceTypeLabels[src]}
                </button>
              ))}
            </div>
          </div>

          {/* Source URL (only for external) */}
          {sourceType !== 'upload' && (
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                URL de {sourceType === 'youtube' ? 'YouTube' : 'Facebook'}
              </label>
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder={sourceType === 'youtube' ? 'https://youtube.com/watch?v=...' : 'https://facebook.com/watch?v=...'}
                className={`w-full px-3 py-2.5 rounded-xl bg-background-100 border text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors ${
                  errors.sourceUrl ? 'border-red-400' : 'border-background-200'
                }`}
              />
              {errors.sourceUrl && <p className="text-red-500 text-[10px] mt-1">{errors.sourceUrl}</p>}
            </div>
          )}

          {/* Duración y Estado */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Duración</label>
              <input
                type="text"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="Ej: 1:30:00"
                className={`w-full px-3 py-2.5 rounded-xl bg-background-100 border text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors ${
                  errors.duration ? 'border-red-400' : 'border-background-200'
                }`}
              />
              {errors.duration && <p className="text-red-500 text-[10px] mt-1">{errors.duration}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Estado</label>
              <div className="flex items-center gap-1">
                {(['borrador', 'programado', 'publicado'] as VideoStatus[]).map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => setStatus(st)}
                    className={`flex-1 px-2 py-2 rounded-xl border text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                      status === st
                        ? st === 'publicado'
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : st === 'programado'
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-foreground-300 bg-foreground-100 text-foreground-700'
                        : 'border-background-200 bg-background-100 text-foreground-400 hover:bg-background-200'
                    }`}
                  >
                    {statusLabels[st]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Fecha programada */}
          {status === 'programado' && (
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Fecha de publicación</label>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className={`w-full px-3 py-2.5 rounded-xl bg-background-100 border text-foreground-900 text-sm focus:outline-none focus:border-accent-500 transition-colors ${
                  errors.scheduledAt ? 'border-red-400' : 'border-background-200'
                }`}
              />
              {errors.scheduledAt && <p className="text-red-500 text-[10px] mt-1">{errors.scheduledAt}</p>}
            </div>
          )}

          {/* Destacado */}
          <div className="flex items-center gap-3 py-1">
            <button
              type="button"
              onClick={() => setIsFeatured(!isFeatured)}
              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 cursor-pointer transition-all ${
                isFeatured ? 'bg-amber-500 border-amber-500' : 'bg-transparent border-background-300'
              }`}
            >
              {isFeatured && <i className="ri-check-line text-background-50 text-[10px]"></i>}
            </button>
            <div>
              <p className="text-xs font-semibold text-foreground-800">Marcar como destacado</p>
              <p className="text-[10px] text-foreground-400">Aparecerá primero en la sección de videos</p>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center gap-2.5 px-5 py-4 border-t border-background-200 bg-background-50">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-background-50 bg-accent-500 hover:bg-accent-600 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-1.5">
                <i className="ri-loader-4-line animate-spin"></i>
                Guardando...
              </span>
            ) : (
              isEditing ? 'Guardar cambios' : 'Agregar video'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}